import React, { useState, useRef, useEffect, useContext } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";

import { UserContext } from "./UserContext.js";
import Button from "./components/Button.js";
import Checkbox from "./components/Checkbox.js";
import styles from "./FinaliseOrderScreen.module.css";

import { FaBox } from "react-icons/fa";

import bbbLogo from "./assets/images/bbbLogo.png";
import greenCircles from "./assets/images/greenCirclesCorner.png";
import dots from "./assets/images/patternDots.png";
import successImage from "./assets/images/check.png";

//operators to add haulier details 

const FinaliseOrderScreen = (props) => {
  document.title = "Finalise Order";

  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const sessionBags = location.state?.sessionBags ?? 0;

  const [shipmentSummary, setShipmentSummary] = useState(null);
  const [shipmentLoading, setShipmentLoading] = useState(true);
  const [haulierCompany, setHaulierCompany] = useState("");
  const [driverName, setDriverName] = useState("");
  const [vehicleReg, setVehicleReg] = useState("");
  const [operatorName, setOperatorName] = useState("");
  const [checkedContents, setCheckedContents] = useState(false);
  const [checkedDocs, setCheckedDocs] = useState(false);
  const [errorPopup, setErrorPopup] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const opSigCanvas = useRef(null);
  const containerRef = useRef(null);

  const backEndURL = props.backEndURL || user?.backEndURL;

  const isPopupOpen = !!errorPopup;

  useEffect(() => {
    if (!containerRef.current) return;
    containerRef.current.style.overflow = isPopupOpen || confirmed ? "hidden" : "scroll";
  }, [confirmed, isPopupOpen]);

  useEffect(() => {
    if (!orderId || !backEndURL) return;
    const loadSummary = async () => {
      setShipmentLoading(true);
      try {
        const res = await fetch(backEndURL + "/delivery/shipment-summary/" + orderId);
        const json = await res.json();
        if (json.success) {
          const isFinalised = Boolean(json.data.shipment_finalised_at);
          const augmentedOrders = json.data.orders.map(o =>
            o.is_selected
              ? { ...o, loaded_qty: Math.max(o.loaded_qty, sessionBags) }
              : o
          );
          const totalExpected = augmentedOrders.reduce((s, o) => s + o.expected_qty, 0);
          const total_weight_t = parseFloat(((totalExpected * 220) / 1000).toFixed(2));
          setShipmentSummary({
            ...json.data,
            orders: augmentedOrders,
            total_qty: totalExpected,
            total_weight_t,
          });
          setConfirmed(isFinalised);
        }
      } catch (e) {
        console.error("Failed to load shipment summary", e);
      } finally {
        setShipmentLoading(false);
      }
    };
    loadSummary();
  }, [orderId, backEndURL]);

  useEffect(() => {
    if (!confirmed) return undefined;

    const finalisedRoute = `/order/${encodeURIComponent(orderId)}`;
    window.history.pushState(null, "", window.location.href);
    const keepFinalisedScreenOpen = () => {
      navigate(finalisedRoute, { replace: true });
    };
    window.addEventListener("popstate", keepFinalisedScreenOpen);

    return () => window.removeEventListener("popstate", keepFinalisedScreenOpen);
  }, [confirmed, navigate, orderId]);

  const doFinalise = async () => {
    if (!shipmentSummary?.delivery_db_id) {
      setErrorPopup({
        title: "Cannot finalise",
        body: "Shipment details are unavailable. Please reload and try again.",
        details: [],
      });
      return;
    }

    try {
      const response = await fetch(
        backEndURL + "/delivery/" + shipmentSummary.delivery_db_id + "/finalise",
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            haulier_company: haulierCompany,
            driver_name: driverName,
            vehicle_reg: vehicleReg,
            operator_name: operatorName,
            operator_signature: opSigCanvas.current?.toDataURL() || "",
          }),
        }
      );
      const json = await response.json();
      if (!response.ok || !json.success) {
        throw new Error(json.message || "Failed to finalise shipment.");
      }
      setConfirmed(true);
    } catch (e) {
      console.error("Failed to save haulier details", e);
      setErrorPopup({
        title: "Failed to finalise",
        body: e.message || "The shipment could not be finalised.",
        details: [],
      });
    }
  };

  const confirmShipment = () => {
    if (!opSigCanvas.current || opSigCanvas.current.isEmpty()) {
      setErrorPopup({ title: "Signature required", body: "Please sign before confirming the shipment.", details: [] });
      return;
    }
    if (!checkedContents || !checkedDocs) {
      setErrorPopup({ title: "Confirmation required", body: "Please check both confirmation boxes before proceeding.", details: [] });
      return;
    }

    const hasShortfall = shipmentSummary?.orders?.some(o => o.loaded_qty < o.expected_qty);
    if (hasShortfall) {
      setErrorPopup({
        title: "Cannot finalise",
        body: "All bags must be loaded before the shipment can be finalised. Please scan the remaining bags first.",
        details: [],
      });
      return;
    }

    doFinalise();
  };

  const renderContent = () => {
    if (shipmentLoading) {
      return <div className={styles.lockedMessage}>Loading shipment details...</div>;
    }

    if (confirmed) {
      return (
        <div className={styles.confirmedScreen}>
          <img src={successImage} className={styles.mainIllustration} alt="Success"/>
          <div className={styles.header}>Shipment Confirmed!</div>
          <div className={styles.description}>
            The shipment has been finalised and the driver has been released.
          </div>
          <div className={styles.lockedMessage}>
            This shipment is complete. You can now close this tab.
          </div>
        </div>
      );
    }

    return (
      <>
        <div className={styles.finaliseTitle}>Finalise Shipment</div>
        <div className={styles.finaliseSubtitle}>
          Check order details are correct. Ensure all scanned bags are loaded onto vehicle. Record haulier details and sign to release shipment.
        </div>

        {/* Shipment Details */}
        <div className={styles.finaliseSection}>
          <div className={styles.finaliseSectionHeader}>
            <FaBox /> Shipment Details
          </div>
          {shipmentLoading ? (
            <div className={styles.finaliseLoadingText}>Loading shipment details…</div>
          ) : shipmentSummary ? (
            <>
              <div className={styles.finaliseSummaryRow}>
                <span className={styles.finaliseSummaryLabel}>Delivery ID</span>
                <span className={styles.finaliseSummaryValue}>{shipmentSummary.delivery_id}</span>
              </div>
              <div className={styles.finaliseSummaryRow}>
                <span className={styles.finaliseSummaryLabel}>Total Qty</span>
                <span className={styles.finaliseSummaryValue}>{shipmentSummary.total_qty} bags</span>
              </div>
              <div className={styles.finaliseSummaryRow}>
                <span className={styles.finaliseSummaryLabel}>Total Weight</span>
                <span className={styles.finaliseSummaryValue}>{shipmentSummary.total_weight_t} t</span>
              </div>
              {shipmentSummary.orders.map((o) => (
                <div key={o.order_db_id} className={styles.finaliseOrderCard}>
                  <div className={styles.finaliseOrderInfo}>
                    <div className={styles.finaliseOrderId}><span>Order ID</span><span>{o.order_id}</span></div>
                    <div className={styles.finaliseOrderRow}><span>Recipient</span><span>{o.recipient}</span></div>
                    <div className={styles.finaliseOrderRow}><span>Postcode</span><span>{o.postcode || "—"}</span></div>
                    <div className={styles.finaliseOrderRow}><span>Product</span><span>{o.product || "—"}</span></div>
                  </div>
                  <div className={styles.finaliseCircleWrap}>
                    <svg width="100" height="100" viewBox="0 0 74 74">
                      <circle cx="37" cy="37" r="28" fill="none" stroke="#e0e0e0" strokeWidth="7" />
                      <circle
                        cx="37" cy="37" r="28"
                        fill="none" stroke="#7AC943" strokeWidth="7"
                        strokeDasharray={`${Math.min(o.loaded_qty / Math.max(o.expected_qty, 1), 1) * 2 * Math.PI * 28} ${2 * Math.PI * 28}`}
                        strokeLinecap="round"
                        transform="rotate(-90 37 37)"
                      />
                      <text x="37" y="34" textAnchor="middle" fontWeight="bold" fill="#333" fontFamily="TruenoBd">
                        <tspan fontSize="13">{o.loaded_qty}</tspan><tspan fontSize="10"> / {o.expected_qty}</tspan>
                      </text>
                      <text x="37" y="47" textAnchor="middle" fontSize="7" fill="#888" fontFamily="Roboto">
                        bags loaded
                      </text>
                    </svg>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className={styles.finaliseLoadingText}>Shipment details unavailable</div>
          )}
        </div>

        {/* Haulier Details */}
        <div className={styles.finaliseSection}>
          <div className={styles.finaliseSectionHeader}>
            <span className={styles.finaliseSectionIcon}>truck</span> Haulier Details
          </div>
          <div className={styles.finaliseFieldLabel}>Haulage Company</div>
          <input className={styles.finaliseInput} placeholder="Business Name" value={haulierCompany} onChange={e => setHaulierCompany(e.target.value)} />
          <div className={styles.finaliseFieldLabel}>Driver Name</div>
          <input className={styles.finaliseInput} placeholder="Full Name" value={driverName} onChange={e => setDriverName(e.target.value)} />
          <div className={styles.finaliseFieldLabel}>Vehicle Reg No.</div>
          <input className={styles.finaliseInput} placeholder="Reg No." value={vehicleReg} onChange={e => setVehicleReg(e.target.value)} />
        </div>

        {/* Operator Details */}
        <div className={styles.finaliseSection}>
          <div className={styles.finaliseSectionHeader}>
            <span className={styles.finaliseSectionIcon}>user</span> Operator Details
          </div>
          <div className={styles.finaliseFieldLabel}>Operator Name</div>
          <input className={styles.finaliseInput} placeholder="Full Name" value={operatorName} onChange={e => setOperatorName(e.target.value)} />
          <div className={styles.finaliseFieldLabel}>Operator Signature</div>
          <div className={styles.finaliseSignatureBox}>
            <SignatureCanvas
              penColor="black"
              canvasProps={{ className: styles.finaliseSignatureCanvas }}
              ref={opSigCanvas}
            />
            <div className={styles.finaliseClearSig} onClick={() => opSigCanvas.current?.clear()}>Clear</div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <Checkbox
            thinnerText={false}
            text="I have checked the contents of this shipment and confirm they are correct."
            checked={checkedContents}
            onPress={() => setCheckedContents(v => !v)}
            customStyle={{ fontSize: "1.6rem", height: "auto", display: "flex", alignItems: "center" }}
            checkboxStyle={{ width: "3.2rem", height: "3.2rem", minWidth: "3.2rem", display: "flex", alignItems: "center", justifyContent: "center" }}
            checkStyle={{ width: "2rem", height: "2rem" }}
          />
          <Checkbox
            thinnerText={false}
            text="I have provided the driver with all necessary delivery documentation."
            checked={checkedDocs}
            onPress={() => setCheckedDocs(v => !v)}
            customStyle={{ fontSize: "1.6rem", height: "auto", display: "flex", alignItems: "center" }}
            checkboxStyle={{ width: "3.2rem", height: "3.2rem", minWidth: "3.2rem", display: "flex", alignItems: "center", justifyContent: "center" }}
            checkStyle={{ width: "2rem", height: "2rem" }}
          />
        </div>

        <Button color="Grass" name="Confirm Shipment" onPress={confirmShipment} customStyle={{ marginTop: "10px", width: "100%" }} />
        <Button color="Coal" name="← Back" onPress={() => navigate(-1)} customStyle={{ marginTop: "6px", width: "100%" }} />

        {errorPopup && (
          <div className={styles.errorPopupOverlay}>
            <div className={styles.errorPopupCard}>
              <div className={styles.errorPopupTitle}>{errorPopup.title}</div>
              <div className={styles.errorPopupBody}>{errorPopup.body}</div>
              {errorPopup.details?.length > 0 && (
                <ul className={styles.errorPopupDetails}>
                  {errorPopup.details.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              )}
              <Button
                name="OK"
                onPress={() => setErrorPopup(null)}
                color="Coal"
                customStyle={{ marginTop: "12px", width: "100%" }}
              />
            </div>
          </div>
        )}

</>
    );
  };

  return (
    <div className={styles.fullContainer}>
      <div className={styles.patternBg}/>
      <img src={dots} className={styles.pattern1} alt=""/>
      <img src={dots} className={styles.pattern2} alt=""/>
      <div className={styles.blackContainerWrapper}>
        <div className={styles.logoContainer}>
          <img src={bbbLogo} className={styles.logo} alt="BBB Logo"/>
        </div>
        <div className={styles.blackContainer}>
          <div className={styles.container} ref={containerRef}>
            <img src={greenCircles} className={styles.circle2} alt=""/>
            {renderContent()}
            <img src={greenCircles} className={styles.circle1} alt=""/>
          </div>
        </div>
      </div>
      <div className={styles.websiteLink} onClick={() => window.open("https://blackbullbiochar.com", "_blank").focus()}>
        <span className={styles.fontAwesome}>globe</span> BLACKBULLBIOCHAR.COM
      </div>
    </div>
  );
};

export default FinaliseOrderScreen;
